import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { isEqual } from 'lodash';
import queryString from 'query-string';

import { ContentSwitcherValue } from '../../../types';
import { ContentContext } from '../../context/ContentContext';
import { useContentManagerParams, useContentManagerValues } from '../../hooks';
import {
  getActiveItems,
  getRelatedContent,
  getUniqueParams,
} from '../../utils';

interface Props {
  values: string;
  setSessionLanguages(values: string[]): void;
}

export const ContentManager: React.FC<Props> = ({
  values: pageValues = '',
  setSessionLanguages,
  children,
}) => {
  const { hash } = useLocation();
  const [values, setValues, defaultItems] = useContentManagerValues(pageValues);

  const addAnchor = useRef<boolean>(true);

  // get the params from the url
  const params = useContentManagerParams(pageValues);
  // the sessionSettings are used to store the users choice if they select anything in the ContentSwitcher
  const [sessionSettings, setSessionSettings] = useState<string[]>(
    // todo: this should not update on route change
    Object.values(params),
  );
  // note: activeItems needs to be filled during first render for scroll-to-anchors to work
  const [activeItems, setActiveItems] = useState<Record<string, string>>(
    getActiveItems(values, sessionSettings),
  );

  // active items in values that should be displayed
  useEffect(() => {
    if (values.length) {
      const items = getActiveItems(values, sessionSettings);

      // only add the necessary values to the url
      const uniqueParams = getUniqueParams(items, defaultItems);

      // use the correct URL params if they have changed
      if (!isEqual(uniqueParams, params)) {
        const query = queryString.stringify(uniqueParams);

        let anchor = '';
        const match = hash.match(/#.*(?=\?)|#.*(?=$)/);
        if (match && match[0]) {
          [anchor] = match;
        }

        let target = './';
        if (query) {
          target = `?${query}`;
        }
        // don't add the anchor if the user scrolled up to the settings and selected a setting
        if (addAnchor.current && anchor) {
          target = `${target}${anchor}`;
        }
        if (target) {
          navigate(target, {
            replace: true,
          });
        }
      } else {
        // need to set session languages since this value controls the displayed code in MultiCodeBlocks
        setSessionLanguages(Object.values(items));
        setActiveItems(items);
      }

      setSessionLanguages(Object.values(items));
      setActiveItems(items);
    }
  }, [values, params, sessionSettings, hash]);

  // filter the list of values based on child relations
  const displayedValues: ContentSwitcherValue[] = useMemo(() => {
    if (
      values.length &&
      Object.keys(activeItems).some(key => key === values[0].param)
    ) {
      return getRelatedContent(values, activeItems);
    }

    if (values.length && values[0].param) {
      return getRelatedContent(values, {
        [values[0].param]: values[0].items[0].id,
      });
    }

    return [];
  }, [values, activeItems]);

  const handleSettingsClick = useCallback(
    (value: string | string[], anchor = true) => {
      const unfilteredSettings = typeof value === 'string' ? [value] : value;
      unfilteredSettings.push(...sessionSettings);
      const nextSessionSettings: string[] = [...new Set(unfilteredSettings)];
      addAnchor.current = anchor;
      setSessionSettings(nextSessionSettings);
    },
    [sessionSettings],
  );

  const value = useMemo(
    () => ({
      activeItems,
      defaultItems,
      displayedValues,
      setValues,
      handleSettingsClick,
    }),
    [
      activeItems,
      defaultItems,
      displayedValues,
      setValues,
      handleSettingsClick,
    ],
  );

  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  );
};
